import type { FC } from 'react';
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import { Helmet } from 'react-helmet';
import { useSSRPreviewMode } from '../../bff-api/customers/useSSRPreviewMode';
import { useRecaptchaScriptLoader } from '../../recaptcha/hooks/useRecaptchaScriptLoader';
import { useSLTConfig } from '../../slt/config/SLTConfigProvider';
import { CustomPreference } from '../../utils/api/CustomPreference';
import { shouldAddSupportForIntlDisplayNames } from '../../utils/browser-utils';
import { getReactHelmetPatching } from '../../utils/page-utils';
import {
    getPrefetchUrls,
    getPreloadFontUrls,
    getPreloadStylesheets,
} from '../../utils/performance-utils';
import { isServer } from '../../utils/utils';
import type { PrefetchUrlConfig } from '../../utils/performance-utils';

export const AppHead: FC = () => {
    const isSSRPreviewMode = useSSRPreviewMode();
    const { req } = useServerContext();
    const {
        env: {
            awsRumApplicationId,
            awsRumEnableMonitoring,
            awsRumGuestRoleARN,
            awsRumIdentityPoolId,
        },
        customPreferences,
    } = useSLTConfig();

    const { isRecaptchaEnabled } = useRecaptchaScriptLoader();

    const gtmId = customPreferences[CustomPreference.GOOGLE_TAG_MANAGER_ID];
    const shouldAddIntlDisplayNamesPolyfill =
        isServer && shouldAddSupportForIntlDisplayNames(req.get('user-agent'));

    return (
        <Helmet>
            {getPreloadFontUrls([
                // Only preload the two most important ones, and let the browser handle the rest
                'static/font/TT_Norms_Pro_Normal-english.woff2',
                'static/font/TT_Norms_Pro_Medium-english.woff2',
            ])}
            {/* TODO: Lato will be removed when we finish implementing the new designs */}
            {getPrefetchUrls([
                { hostname: 'fonts.googleapis.com', addCrossOrigin: true },
                { hostname: 'fonts.gstatic.com', addCrossOrigin: true },
                ...(isRecaptchaEnabled
                    ? [{ hostname: 'www.google.com', addCrossOrigin: false }]
                    : []),
                ...urlsToPrefetch,
            ])}
            {getPreloadStylesheets([
                'https://fonts.googleapis.com/css?family=Lato&subset=latin,latin-ext&display=swap',
            ])}

            {/* This is a hack to ensure that the print styles are applied immediately */}
            <script>
                {
                    "document.querySelectorAll('link[media=\"print\"]').forEach(link => { link.media = 'all' });"
                }
            </script>

            {shouldAddIntlDisplayNamesPolyfill ? (
                <script
                    type="text/javascript"
                    src="https://cdnjs.cloudflare.com/polyfill/v3/polyfill.min.js?features=Intl.DisplayNames,Intl.DisplayNames.~locale.en"
                    async
                />
            ) : null}

            {/* AWS Rum Monitoring */}
            {String(awsRumEnableMonitoring) === 'true' && (
                <script type="text/javascript" id="awsRumCode">
                    {`(function(n,i,v,r,s,c,x,z){x=window.AwsRumClient={q:[],n:n,i:i,v:v,r:r,c:c};window[n]=function(c,p){x.q.push({c:c,p:p});};z=document.createElement('script');z.async=true;z.src=s;document.head.insertBefore(z,document.head.getElementsByTagName('script')[0]);})(
                            'cwr',
                            '${awsRumApplicationId}',
                            '1.0.0',
                            'us-west-2',
                            'https://client.rum.us-east-1.amazonaws.com/1.14.0/cwr.js',
                            {
                            sessionSampleRate: 1,
                            guestRoleArn: '${awsRumGuestRoleARN}',
                            identityPoolId: '${awsRumIdentityPoolId}',
                            endpoint: 'https://dataplane.rum.us-west-2.amazonaws.com',
                            telemetries: ['errors','http'],
                            allowCookies: true,
                            enableXRay: false
                            }
                        );`}
                </script>
            )}

            {/* TODO: Remove JQuery
             ** JQuery is needed because of scripts loaded by gtm & rio
             ** GTM scripts are loaded at the bottom of the head, so we need to load JQuery first
             ** as a blocking script to ensure it is loaded before the other scripts
             */}
            <script
                src="https://code.jquery.com/jquery-3.7.0.slim.min.js"
                integrity="sha256-tG5mcZUtJsZvyKAxYLVXrmjKBVLd6VpVccqz/r4ypFE="
                crossOrigin="anonymous"
            />

            {gtmId && !isSSRPreviewMode ? (
                <script>
                    {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
                            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
                            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
                            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
                        })(window,document,'script','dataLayer','${gtmId}');`}
                </script>
            ) : null}

            <script>
                {`window.setABTestVariable = function(key, value) {
                    window[key] = value;
                    window.dispatchEvent(new CustomEvent('slt-abtest-variable-changed-' + key, { detail: value }));
                }`}
            </script>
            {/* FigPii Asynchronous Tracking Code  */}
            <script>
                {
                    "!function(w,d,u){var n=d.createElement('script');n.crossOrigin='anonymous',n.async='async',n.src=u,d.head.append(n),w._fpEvent=w._fpEvent||[]}(window,document,'https://tracking-cdn.figpii.com/d40ba4a986f94f31452a835eb461f84b.js');"
                }
            </script>
            {/* End FigPii Asynchronous Tracking Code */}

            {/* Util to check if user is logged in for pixel scripts */}
            <script type="text/javascript">
                {`window.isUserLoggedIn = function() {
                        var _window$pageData;
                        return (
                            ((_window$pageData = window.pageData) === null ||
                            _window$pageData === void 0 ||
                            (_window$pageData = _window$pageData.user) === null ||
                            _window$pageData === void 0
                            ? void 0
                            : _window$pageData.authType) === "registered"
                        );
                    };`}
            </script>

            {getReactHelmetPatching()}
        </Helmet>
    );
};

const urlsToPrefetch: PrefetchUrlConfig[] = [
    { hostname: 'code.jquery.com', addCrossOrigin: false },
    { hostname: 'client.rum.us-east-1.amazonaws.com', addCrossOrigin: false },
    { hostname: 'cognito-identity.us-west-2.amazonaws.com', addCrossOrigin: false },
    { hostname: 's3.amazon.com', addCrossOrigin: false },
    { hostname: 'api.radar.io', addCrossOrigin: false },
];
